import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classes from "./VigourVariableRateSlider.module.css";
import { Stack, Slider, Divider, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import { VariableRates, VIGOUR_CLASSES } from "./VigourTaskmap";

export default function VigourVariableRateSlider({
  onRatesChange,
  rates,
}: {
  onRatesChange: (value: VariableRates) => void;
  rates: VariableRates;
}): React.ReactNode {
  const { t } = useTranslation();

  const sliders = VIGOUR_CLASSES.map((vigourClass) => ({
    vigourClass,
    name: `vigour-class-${vigourClass}`,
    title: t("taskmap.label.class", { n: vigourClass }),
  }));

  const handleDepthChange = (vigourClass: number, value: number) => {
    rates.set(vigourClass, value);
    // new instance required for React to re-render other components that depend on this value
    onRatesChange(new Map(rates));
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "fit-content",
          maxWidth: "100%",
        }}
      ></Box>
      <span
        className={classes.sliderVerticalAxisGrid}
        style={{ textAlign: "center", display: "block", width: "100%" }}
      >
        {t("taskmap.vigour.label.depth")}
      </span>
      <Stack
        spacing={{ xs: 0, sm: 0, md: 1 }}
        direction="row"
        className={classes.sliderStack}
        sx={{
          overflowX: "auto",
          width: "100%",
          display: "flex",
          flexWrap: "nowrap",
          justifyContent: "center",
        }}
      >
        {sliders.map((slider) => (
          <DepthSlider
            key={slider.name}
            name={slider.name}
            title={slider.title}
            vigourClass={slider.vigourClass}
            setDepth={handleDepthChange}
          />
        ))}
      </Stack>
    </>
  );
}

const DepthSlider = ({
  name,
  title,
  setDepth,
  vigourClass,
}: {
  vigourClass: number;
  name: string;
  title: string;
  setDepth: (vigourClass: number, value: number) => void;
}): React.ReactNode => {
  const [value, setValue] = useState<number>(0);

  const handleChange = (_: Event, newValue: number) => {
    setValue(newValue);
    setDepth(vigourClass, newValue);
  };
  return (
    <Stack
      spacing={1}
      sx={{
        alignItems: "center",
      }}
      className={classes.stackIndividualSlider}
    >
      <Slider
        aria-label={title}
        orientation="vertical"
        getAriaValueText={() => value.toString()}
        valueLabelDisplay="auto"
        defaultValue={0}
        shiftStep={5} // used when using keyboard to change value
        step={5}
        marks
        min={0}
        max={100}
        name={name}
        onChange={handleChange}
      />
      <span style={{ marginTop: "1rem" }}>{value}%</span>
      <Divider className={classes.sliderDivider} />
      {value > 0 ? <CheckCircleIcon color="success" /> : <CircleOutlinedIcon />}
      <span className={classes.sliderLabel}>{title}</span>
    </Stack>
  );
};
